import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getQuestionsList, getQuestionsMinimal, deleteQuestion, setQuestionsPage } from '../../actions/questionActions';
import { getTags } from '../../actions/tagActions';
import ContentManagementSidebar from '../content-management/ContentManagementSidebar';
import TableView from '../content-management/views/TableView';
import MobileView from '../content-management/views/MobileView';
import TableSkeleton from '../content-management/views/TableView/TableSkeleton';
import MobileSkeleton from '../content-management/views/MobileView/MobileSkeleton';
import DeleteConfirmationModal from '../content-management/common/DeleteConfirmationModal';
import PaginationControls from '../common/PaginationControls';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';
import { Plus, Filter, X } from 'lucide-react';
import { loadContentManagementFilters, saveContentManagementFilters } from '../../utils/contentManagementFilters';
import { isAdminRole } from '../../utils/roleHelpers';

const QuestionsManagementContainer = ({
  auth: { user },
  question: { questions, minimalQuestions, loading, currentPage, totalPages, totalQuestions, hasNextPage, hasPrevPage },
  tag: { tags },
  getQuestionsList,
  getQuestionsMinimal,
  deleteQuestion,
  getTags,
  setQuestionsPage
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersLoadedRef = useRef(false);

  // Admin-only feature
  const isAdmin = isAdminRole(user?.role);

  useEffect(() => {
    getTags();
    getQuestionsMinimal();
  }, [getTags, getQuestionsMinimal]);

  useEffect(() => {
    const saved = loadContentManagementFilters('Questions');
    if (saved?.filters) {
      setSelectedQuestions(saved.filters.selectedQuestions || []);
      setSelectedTags(saved.filters.selectedTags || []);
      if (saved.count > 0) {
        setShowFilters(true);
      }
    }
    filtersLoadedRef.current = true;
    setFiltersHydrated(true);
  }, []);

  useEffect(() => {
    if (!filtersHydrated) return;
    const filters = {};
    if (selectedQuestions.length > 0) {
      filters.question = selectedQuestions;
    }
    if (selectedTags.length > 0) {
      filters.tags = selectedTags;
    }
    getQuestionsList(currentPage, 10, filters);
  }, [filtersHydrated, currentPage, selectedQuestions, selectedTags, getQuestionsList]);

  useEffect(() => {
    if (!filtersLoadedRef.current) return;
    const count = selectedQuestions.length + selectedTags.length;
    saveContentManagementFilters('Questions', { selectedQuestions, selectedTags }, count);
  }, [selectedQuestions, selectedTags]);

  const handleAdd = () => {
    navigate('/content-management/questions/new');
  };

  const handleView = (item) => {
    navigate(`/content-management/questions/${item._id}/view`);
  };

  const handleEdit = (item) => {
    navigate(`/content-management/questions/${item._id}/edit`);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    deleteQuestion(itemToDelete._id);
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Questions') {
      // Already on questions page, do nothing
      return;
    }
    // Navigate to content management with selected tab
    navigate('/content-management', { state: { activeTab: tabId } });
  };

  const getItemTitle = (item) => {
    return item.text;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Handle pagination - only update Redux state, useEffect will fetch data
  const handlePageChange = (newPage) => {
    setQuestionsPage(newPage);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearAllFilters = () => {
    setSelectedQuestions([]);
    setSelectedTags([]);
    setQuestionsPage(1);
  };

  const hasActiveFilters = selectedQuestions.length > 0 || selectedTags.length > 0;

  // Get unique question texts from questions
  const uniqueQuestions = minimalQuestions && minimalQuestions.length > 0
    ? [...new Set(minimalQuestions.map(q => q.text))].filter(Boolean)
    : [];

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Questions"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={false}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-[#054581]">
                  Manage Questions
                </h2>
                {totalQuestions > 0 && (
                  <span className="text-sm text-gray-500">
                    ({totalQuestions} total)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    hasActiveFilters
                      ? 'bg-[#1086a4] text-white shadow-md'
                      : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                  }`}
                >
                  <Filter size={18} />
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  {hasActiveFilters && (
                    <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                      Active
                    </span>
                  )}
                </button>
                <button
                  onClick={handleAdd}
                  className="bg-[#1086a4] text-white px-2 sm:px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  <span>Add</span>
                </button>
              </div>
            </div>

            {/* Filter UI */}
            {showFilters && (
              <div className="mb-6 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#054581]">Filter Questions</h3>
                  <button
                    onClick={clearAllFilters}
                    disabled={!hasActiveFilters}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      hasActiveFilters
                        ? 'bg-red-500 hover:bg-red-600 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <X className="w-3 h-3" />
                    Clear All Filters
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <MultiSelectSearchFilter
                    items={uniqueQuestions.map(text => ({ _id: text, title: text }))}
                    selectedItems={selectedQuestions}
                    onSelectionChange={(selected) => {
                      setSelectedQuestions(selected);
                      setQuestionsPage(1);
                    }}
                    title="Question Text"
                    placeholder="Search questions..."
                    emptyMessage="No questions available"
                  />

                  <MultiSelectSearchFilter
                    items={tags}
                    selectedItems={selectedTags}
                    onSelectionChange={(selected) => {
                      setSelectedTags(selected);
                      setQuestionsPage(1);
                    }}
                    title="Tags"
                    placeholder="Search tags..."
                    emptyMessage="No tags available"
                  />
                </div>
              </div>
            )}

            {loading ? (
              <>
                {/* Desktop Skeleton */}
                <div className="hidden md:block">
                  <TableSkeleton rowCount={10} activeTab="Questions" />
                </div>

                {/* Mobile Skeleton */}
                <div className="md:hidden">
                  <MobileSkeleton cardCount={10} />
                </div>
              </>
            ) : (
              <>
                {/* Mobile View */}
                <div className="md:hidden">
                  <MobileView
                    items={questions}
                    activeTab="Questions"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={false}
                  />
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                  <TableView
                    items={questions}
                    activeTab="Questions"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={false}
                  />
                </div>

                {/* Pagination Controls */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalQuestions}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  itemName="questions"
                />
              </>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
              isOpen={showDeleteConfirmation}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              itemTitle={itemToDelete ? getItemTitle(itemToDelete) : ''}
              itemType="Question"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  question: state.question,
  tag: state.tag
});

export default connect(
  mapStateToProps,
  {
    getQuestionsList,
    getQuestionsMinimal,
    deleteQuestion,
    getTags,
    setQuestionsPage
  }
)(QuestionsManagementContainer);
